import React, { useEffect, useState } from "react";
import { BarChart3, Users, Landmark, Euro, CheckCircle2, FolderOpen, Tag } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { api } from "../lib/api";
import AiInsight from "../components/AiInsight";
import { TAG_COLORS } from "../components/DossierTags";

const BAR = { red: "bg-red-400", orange: "bg-orange-400", amber: "bg-amber-400", emerald: "bg-emerald-400", teal: "bg-teal-400", sky: "bg-sky-400", indigo: "bg-indigo-400", violet: "bg-violet-400", rose: "bg-rose-400", slate: "bg-slate-400" };

const PERIODS = [
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
  { days: 365, label: "12 mois" },
  { days: null, label: "Tout" },
];

const eur = (v) => `${Math.round(v || 0).toLocaleString("fr-FR")} €`;

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="overline mb-2 flex items-center gap-1.5"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-heading text-2xl text-norea-text">{value}</div>
    </div>
  );
}

function RateBar({ value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 rounded-full bg-norea-border overflow-hidden">
        <div className="h-full bg-norea-primary" style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className="text-xs tabular-nums text-norea-text">{value}%</span>
    </div>
  );
}

function GroupTable({ title, icon: Icon, rows, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="overline mb-4 flex items-center gap-1.5"><Icon className="w-3 h-3" /> {title}</div>
      <div className="h-48 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E2" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={36} />
            <Tooltip formatter={(v) => eur(v)} />
            <Bar dataKey="montant_total" name="Montant" fill="#2E4734" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-norea-textMuted text-left border-b border-norea-border">
            <th className="py-2 font-semibold">Nom</th>
            <th className="py-2 font-semibold text-right">Dossiers</th>
            <th className="py-2 font-semibold pl-4">Taux complets</th>
            <th className="py-2 font-semibold text-right">Pièces manq. moy.</th>
            <th className="py-2 font-semibold text-right">Montant total</th>
            <th className="py-2 font-semibold text-right">Panier moyen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.name} className="border-b border-norea-border/60 last:border-0" data-testid={`${testid}-row-${i}`}>
              <td className={`py-2.5 ${r.name === "Non renseigné" ? "italic text-norea-textMuted" : "font-medium text-norea-text"}`}>
                {r.name}
                <span className="ml-2 text-[10px] text-norea-textMuted">
                  {Object.entries(r.produits).map(([p, n]) => `${p} ${n}`).join(" · ")}
                </span>
              </td>
              <td className="py-2.5 text-right tabular-nums">{r.dossiers}</td>
              <td className="py-2.5 pl-4"><RateBar value={r.taux_complet} /></td>
              <td className="py-2.5 text-right tabular-nums">{r.pieces_manquantes_moy}</td>
              <td className="py-2.5 text-right tabular-nums font-medium">{eur(r.montant_total)}</td>
              <td className="py-2.5 text-right tabular-nums">{eur(r.panier_moyen)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="py-6 text-center text-norea-textMuted italic">Aucun dossier sur la période</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function HBar({ label, count, pct, color, strong, pill, testid }) {
  return (
    <div className="grid grid-cols-[160px_1fr_90px] items-center gap-3" data-testid={testid}>
      {pill ? (
        <span className={`justify-self-start inline-flex rounded-full border text-[11px] px-2 py-0.5 font-semibold truncate max-w-full ${TAG_COLORS[color] || TAG_COLORS.slate}`}>{label}</span>
      ) : (
        <span className={`text-sm truncate ${strong ? "font-semibold text-norea-text" : "text-norea-text"}`}>{label}</span>
      )}
      <div className={`${strong ? "h-3" : "h-2"} rounded-full bg-norea-border/60 overflow-hidden`}>
        <div className={`h-full rounded-full ${BAR[color] || BAR.slate} transition-[width] duration-700`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="text-xs tabular-nums text-right text-norea-text"><strong>{count}</strong> · {pct}%</span>
    </div>
  );
}

function TagStats({ families }) {
  return (
    <div className="card-flat p-5" data-testid="stats-tags">
      <div className="overline mb-4 flex items-center gap-1.5"><Tag className="w-3 h-3" /> Par tag</div>
      {families.length === 0 ? (
        <div className="py-6 text-center text-sm text-norea-textMuted italic">Aucune famille de tags configurée</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-10 gap-y-6">
          {families.map((f) => (
            <div key={f.id} className="space-y-2" data-testid={`stats-tag-family-${f.id}`}>
              <HBar label={f.name} count={f.count} pct={f.pct} color={f.color} strong testid={`stats-family-bar-${f.id}`} />
              <div className="pl-3 border-l-2 border-norea-border space-y-1.5">
                {f.tags.map((t) => <HBar key={t.id} label={t.name} count={t.count} pct={t.pct} color={f.color} pill testid={`stats-tag-bar-${t.id}`} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StatsPage() {
  const [days, setDays] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/stats/regies-financiers", { params: days ? { days } : {} }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);

  const t = data?.totals;
  return (
    <AppLayout>
      <PageHeader
        title="Statistiques"
        subtitle="Volume, complétude et montants par régie commerciale et par partenaire financier."
        actions={
          <div className="flex items-center gap-1 p-1 rounded-md border border-norea-border bg-white" data-testid="stats-period">
            {PERIODS.map((p) => (
              <button
                key={p.label}
                onClick={() => setDays(p.days)}
                className={`px-3 py-1 text-xs rounded transition-colors ${days === p.days ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`}
                data-testid={`stats-period-${p.days || "all"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        }
      />
      {!data ? (
        <div className="text-sm text-norea-textMuted" data-testid="stats-loading">Chargement…</div>
      ) : (
        <div className="space-y-5" data-testid="stats-page">
          <AiInsight scope="stats" refId={days ? String(days) : "all"} title="Lecture IA des statistiques" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Dossiers" value={t.dossiers} icon={FolderOpen} testid="kpi-dossiers" />
            <Kpi label="Taux de complets" value={`${t.taux_complet}%`} icon={CheckCircle2} testid="kpi-taux" />
            <Kpi label="Montant total" value={eur(t.montant_total)} icon={Euro} testid="kpi-montant" />
            <Kpi label="Panier moyen" value={eur(t.panier_moyen)} icon={BarChart3} testid="kpi-panier" />
          </div>
          <GroupTable title="Par régie commerciale" icon={Users} rows={data.regies} testid="stats-regies" />
          <GroupTable title="Par partenaire financier" icon={Landmark} rows={data.financiers} testid="stats-financiers" />
          <TagStats families={data.tags || []} />
        </div>
      )}
    </AppLayout>
  );
}
